import { useState, useEffect } from 'react';
import { FiUsers, FiUserPlus, FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiArrowLeft, FiMoreVertical, FiUserX, FiShield, FiKey } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { organizationAPI } from '../../api/organizations';
import { useOrganization } from '../../contexts/OrganizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { InviteModal } from '../../components/InviteModal';
import { InvitationsList } from '../../components/InvitationsList';
import { useToast } from '../../hooks/useToast';
import { ConfirmModal } from '../../components/Modal/Modal';
import styles from './TeamMembers.module.css';

import { logger } from '../../utils/logger';

interface Member {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joined_at: string;
  is_active: boolean;
}

// Response matches backend API exactly
interface MembersResponse {
  organization_id: string;
  members: Member[];
  total: number;
  limit: number;
  offset: number;
}

export default function TeamMembers() {
  const { currentOrganization } = useOrganization();
  const { canInviteMembers, canUpdateMemberRoles, canRemoveMembers, currentRole } = usePermissions();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [members, setMembers] = useState<Member[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Filters
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Invitation modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Bumped on every successful invite so the InvitationsList rendered below
  // reloads. Every invite now creates a pending row regardless of whether the
  // email already had an account (no more force-join), so there's no longer
  // a response field to gate a conditional refresh on - see handleInvite.
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);

  // Member actions
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  // P1.2: in-app confirmation dialog in place of window.confirm().
  const [removeTarget, setRemoveTarget] = useState<{ id: string; email: string } | null>(null);
  // Recovery for a member who lost both their authenticator and their backup
  // codes. Confirmed rather than immediate: it removes a security control from
  // someone else's account.
  const [resetMfaTarget, setResetMfaTarget] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    if (currentOrganization?.id) {
      loadMembers();
    }
    // loadMembers is intentionally excluded; it closes over the same deps we list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id, currentPage, roleFilter]);

  // Close dropdown when clicking outside, scrolling, or resizing (fixed-position anchoring would drift otherwise).
  useEffect(() => {
    if (!activeDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(`.${styles.actionsCell}`) && !target.closest(`.${styles.actionsDropdown}`)) {
        setActiveDropdown(null);
        setDropdownPosition(null);
      }
    };

    const closeDropdown = () => {
      setActiveDropdown(null);
      setDropdownPosition(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', closeDropdown, true);
    window.addEventListener('resize', closeDropdown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', closeDropdown, true);
      window.removeEventListener('resize', closeDropdown);
    };
  }, [activeDropdown]);

  const loadMembers = async () => {
    if (!currentOrganization?.id) return;

    try {
      setLoading(true);
      setError(null);

      const offset = (currentPage - 1) * pageSize;
      const params: { limit: number; offset: number; role?: string } = { limit: pageSize, offset };

      if (roleFilter) {
        params.role = roleFilter;
      }

      const response: MembersResponse = await organizationAPI.getOrganizationMembers(
        currentOrganization.id,
        params
      );

      setMembers(response.members);
      setTotal(response.total);
    } catch (err) {
      setError('Failed to load team members');
      logger.error('Error loading members:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (data: { email: string; role: 'member' | 'admin' | 'viewer' }) => {
    if (!currentOrganization?.id || inviteLoading) return;

    try {
      setInviteLoading(true);
      setInviteError(null); // Clear previous errors
      await organizationAPI.inviteUserToOrganization(currentOrganization.id, {
        email: data.email,
        role: data.role,
      });

      setShowInviteModal(false);
      setInviteError(null); // Clear error on success
      addToast(`Invitation sent to ${data.email}`, 'success');

      // Every invite creates a pending invitation now, consent is required
      // even for an email that already has an account - so this is the only
      // place an admin sees the person they just invited. Bump unconditionally.
      setInvitationsRefreshKey((key) => key + 1);
    } catch (err) {
      // Extract error message from API response
      const errorMessage = err instanceof Error ? err.message : 'Failed to send invitation';
      setInviteError(errorMessage);
      // Don't show toast - error is displayed in modal
      throw err; // Re-throw to prevent modal from closing
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRoleChange = async (memberId: string, memberEmail: string, newRole: 'owner' | 'admin' | 'member' | 'viewer') => {
    if (!currentOrganization?.id) return;

    // Only owner can assign owner role
    if (newRole === 'owner' && currentRole !== 'owner') {
      addToast('Only the organization owner can assign the owner role', 'error');
      return;
    }

    try {
      setActionLoading(memberId);
      await organizationAPI.updateMemberRole(currentOrganization.id, memberId, { role: newRole });
      addToast(`Role updated to ${newRole} for ${memberEmail}`, 'success');
      loadMembers();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to update role', 'error');
    } finally {
      setActionLoading(null);
      setActiveDropdown(null);
    }
  };

  const requestRemoveMember = (memberId: string, memberEmail: string, memberRole: string) => {
    if (!currentOrganization?.id) return;

    // Cannot remove owner
    if (memberRole === 'owner') {
      addToast('Cannot remove the organization owner', 'error');
      return;
    }

    // Cannot remove yourself
    if (memberId === user?.id) {
      addToast('You cannot remove yourself from the organization', 'error');
      return;
    }

    setActiveDropdown(null);
    setRemoveTarget({ id: memberId, email: memberEmail });
  };

  const confirmRemoveMember = async () => {
    if (!currentOrganization?.id || !removeTarget) return;

    const { id: memberId, email: memberEmail } = removeTarget;

    try {
      setActionLoading(memberId);
      await organizationAPI.removeMemberFromOrganization(currentOrganization.id, memberId);
      addToast(`${memberEmail} has been removed from the organization`, 'success');
      loadMembers();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to remove member', 'error');
    } finally {
      setActionLoading(null);
      setRemoveTarget(null);
    }
  };

  const confirmResetMfa = async () => {
    if (!currentOrganization?.id || !resetMfaTarget) return;

    const { id: memberId, email: memberEmail } = resetMfaTarget;

    try {
      setActionLoading(memberId);
      await organizationAPI.resetMemberMfa(currentOrganization.id, memberId);
      addToast(
        `Two-factor authentication reset for ${memberEmail}. They can set it up again from Settings, then Security.`,
        'success',
      );
      loadMembers();
    } catch (err) {
      // The server's refusals name what to do instead ("only an owner can…",
      // "use Settings then Security…"), so show them as-is. The frontend guards
      // in canModifyMember should prevent most of these being reachable, but the
      // server is the authority and its wording is the useful part.
      addToast(
        err instanceof Error ? err.message : 'Failed to reset two-factor authentication',
        'error',
      );
    } finally {
      setActionLoading(null);
      setResetMfaTarget(null);
    }
  };

  // Get available roles for dropdown based on current user's role
  const getAvailableRoles = (): Array<'owner' | 'admin' | 'member' | 'viewer'> => {
    if (currentRole === 'owner') {
      return ['owner', 'admin', 'member', 'viewer'];
    }
    // Admin can only assign admin, member, viewer (not owner)
    return ['admin', 'member', 'viewer'];
  };

  // Check if current user can modify a specific member
  const canModifyMember = (member: Member): boolean => {
    // Cannot modify yourself
    if (member.user_id === user?.id) return false;

    // Only owner can modify another owner
    if (member.role === 'owner' && currentRole !== 'owner') return false;

    // Admin can modify member/viewer, owner can modify anyone
    return canUpdateMemberRoles() || canRemoveMembers();
  };

  const totalPages = Math.ceil(total / pageSize);

  const filteredMembers = searchQuery
    ? members.filter(member =>
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : members;

  const getRoleBadgeVariant = (role: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'danger' | 'tertiary' => {
    switch (role) {
      case 'owner': return 'primary';
      case 'admin': return 'info';
      case 'member': return 'secondary';
      case 'viewer': return 'tertiary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className={styles.teamMembersPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <Link to="/admin" className={styles.backButton}>
            <FiArrowLeft />
            Back to Admin
          </Link>
          <div>
            <div className={styles.titleSection}>
              <FiUsers className={styles.headerIcon} />
              <div>
                <h1>Team Members</h1>
                <p>Manage your organization's team members and roles</p>
              </div>
            </div>

            {canInviteMembers() && (
              <Button variant="primary" onClick={() => setShowInviteModal(true)}>
                <FiUserPlus />
                Invite Member
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className={styles.filtersCard}>
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.roleFilter}>
            <FiFilter className={styles.filterIcon} />
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1); // Reset to first page when filtering
              }}
              className={styles.roleSelect}
            >
              <option value="">All Roles</option>
              <option value="owner">Owners</option>
              <option value="admin">Admins</option>
              <option value="member">Members</option>
              <option value="viewer">Viewers</option>
            </select>
          </div>

          <div className={styles.memberCount}>
            {total} {total === 1 ? 'member' : 'members'}
          </div>
        </div>
      </Card>

      {/* Members Table */}
      <Card className={styles.membersCard}>
        {loading ? (
          <div className={styles.loading}>Loading members...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : filteredMembers.length === 0 ? (
          <div className={styles.empty}>
            <FiUsers className={styles.emptyIcon} />
            <p>No members found</p>
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.membersTable}>
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th>Status</th>
                    {(canUpdateMemberRoles() || canRemoveMembers()) && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member) => (
                    <tr key={member.user_id}>
                      <td>
                        <div className={styles.memberInfo}>
                          <div className={styles.avatar}>
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </div>
                          <div className={styles.memberName}>
                            {member.first_name} {member.last_name}
                            {member.user_id === user?.id && (
                              <span className={styles.youBadge}>(you)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{member.email}</td>
                      <td>
                        <Badge variant={getRoleBadgeVariant(member.role)}>
                          {member.role}
                        </Badge>
                      </td>
                      <td>{formatDate(member.joined_at)}</td>
                      <td>
                        <Badge variant={member.is_active ? 'success' : 'warning'}>
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      {(canUpdateMemberRoles() || canRemoveMembers()) && (
                        <td>
                          {canModifyMember(member) ? (
                            <div className={styles.actionsCell}>
                              <button
                                className={styles.actionsButton}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  if (activeDropdown === member.user_id) {
                                    setActiveDropdown(null);
                                    setDropdownPosition(null);
                                    return;
                                  }

                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const viewportWidth = window.innerWidth;
                                  const viewportHeight = window.innerHeight;
                                  const dropdownWidth = 200;
                                  const margin = 8;

                                  // Estimate dropdown height so we can flip up when there isn't room below.
                                  // Structure: section label + role buttons + optional dividers +
                                  // reset-2FA entry + remove entry + padding.
                                  // Keep this in sync with the JSX below — an entry added here
                                  // without updating the estimate makes the dropdown render
                                  // partly off-screen near the bottom of the viewport.
                                  const roleCount = currentRole === 'owner' ? 4 : 3;
                                  const showRoles = canUpdateMemberRoles();
                                  const showResetMfa = canUpdateMemberRoles();
                                  const showRemove = canRemoveMembers() && member.role !== 'owner';
                                  const estimatedHeight =
                                    16 + // vertical padding
                                    (showRoles ? 28 + roleCount * 36 : 0) +
                                    (showResetMfa ? 9 + 36 : 0) + // divider + entry
                                    (showRemove ? 9 + 36 : 0); // divider + entry

                                  const spaceBelow = viewportHeight - rect.bottom - margin;
                                  const openUpward = spaceBelow < estimatedHeight && rect.top > estimatedHeight + margin;

                                  const top = openUpward
                                    ? Math.max(margin, rect.top - estimatedHeight - 4)
                                    : Math.min(viewportHeight - estimatedHeight - margin, rect.bottom + 4);

                                  const left = Math.max(
                                    margin,
                                    Math.min(viewportWidth - dropdownWidth - margin, rect.right - dropdownWidth)
                                  );

                                  // position: fixed is viewport-relative, so no scroll offsets.
                                  setDropdownPosition({ top, left });
                                  setActiveDropdown(member.user_id);
                                }}
                                disabled={actionLoading === member.user_id}
                              >
                                <FiMoreVertical />
                              </button>

                              {activeDropdown === member.user_id && dropdownPosition && (
                                <div
                                  className={styles.actionsDropdown}
                                  style={{
                                    position: 'fixed',
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  {canUpdateMemberRoles() && (
                                    <>
                                      <div className={styles.dropdownSection}>
                                        <span className={styles.dropdownLabel}>
                                          <FiShield size={12} />
                                          Change Role
                                        </span>
                                        {getAvailableRoles().map((role) => (
                                          <button
                                            key={role}
                                            className={`${styles.dropdownItem} ${member.role === role ? styles.active : ''}`}
                                            onClick={() => handleRoleChange(member.user_id, member.email, role)}
                                            disabled={member.role === role || actionLoading === member.user_id}
                                          >
                                            <Badge variant={getRoleBadgeVariant(role)} size="sm">
                                              {role}
                                            </Badge>
                                            {member.role === role && <span className={styles.currentLabel}>Current</span>}
                                          </button>
                                        ))}
                                      </div>
                                    </>
                                  )}

                                  {canUpdateMemberRoles() && (
                                    <>
                                      <div className={styles.dropdownDivider} />
                                      <button
                                        className={styles.dropdownItem}
                                        onClick={() => setResetMfaTarget({ id: member.user_id, email: member.email })}
                                        disabled={actionLoading === member.user_id}
                                        title="For a member who has lost their authenticator app and their backup codes"
                                      >
                                        <FiKey size={14} />
                                        Reset 2FA
                                      </button>
                                    </>
                                  )}

                                  {canRemoveMembers() && member.role !== 'owner' && (
                                    <>
                                      <div className={styles.dropdownDivider} />
                                      <button
                                        className={`${styles.dropdownItem} ${styles.danger}`}
                                        onClick={() => requestRemoveMember(member.user_id, member.email, member.role)}
                                        disabled={actionLoading === member.user_id}
                                      >
                                        <FiUserX size={14} />
                                        Remove Member
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className={styles.noActions}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <Button
                  variant="secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  <FiChevronLeft />
                  Previous
                </Button>

                <div className={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>

                <Button
                  variant="secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  Next
                  <FiChevronRight />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Pending invitations live inside the members card, after the table/
            pagination block, so they stay visible regardless of the loading,
            error, or filtered-empty state above and admins see pending people
            in the same place they look for members rather than as a separate
            section further down the page. */}
        {currentOrganization?.id && (
          <InvitationsList
            organizationId={currentOrganization.id}
            onRefresh={loadMembers}
            heading="Pending invitations"
            refreshKey={invitationsRefreshKey}
          />
        )}
      </Card>

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setInviteError(null); // Clear error when closing modal
        }}
        onSubmit={handleInvite}
        isLoading={inviteLoading}
        error={inviteError}
      />

      <ConfirmModal
        isOpen={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemoveMember}
        title="Remove member"
        message={
          removeTarget
            ? `Are you sure you want to remove ${removeTarget.email} from the organization?`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmModal
        isOpen={resetMfaTarget !== null}
        onClose={() => setResetMfaTarget(null)}
        onConfirm={confirmResetMfa}
        title="Reset two-factor authentication"
        message={
          resetMfaTarget
            ? `This turns off two-factor authentication for ${resetMfaTarget.email} and invalidates their backup codes. They will be able to sign in with just their password until they set it up again. Only do this if they have lost both their authenticator app and their backup codes.`
            : ''
        }
        confirmText="Reset 2FA"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}

